import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { UserSummaryType } from '../../../../../../api/types/user/user-summary.type';
import { InstallmentBankTransferWithPlanItemsDetails } from '../../../../../../api/types/students/user-details-student';
import { updateInstallmentPlanBankAccountDetailsBankTransferOperation } from '../../operations/update-installment-plan-bank-account-details-bank-transfer-operation';

interface UpdateBankAccountDetailsDialogProps {
  visible: boolean;
  user: UserSummaryType;
  plan: InstallmentBankTransferWithPlanItemsDetails | null;
  onHide: () => void;
}

const isDigitsOnly = (value: string) => /^\d+$/.test(value);

export const UpdateBankAccountDetailsDialog: React.FC<
  UpdateBankAccountDetailsDialogProps
> = ({ visible, user, plan, onHide }) => {
  const [bankNumber, setBankNumber] = useState('');
  const [branchNumber, setBranchNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setBankNumber(plan?.installmentPlan.bankNumber ?? '');
    setBranchNumber(plan?.installmentPlan.branchNumber ?? '');
    setAccountNumber(plan?.installmentPlan.accountNumber ?? '');
  }, [visible, plan]);

  const isValid = useMemo(() => {
    if (!plan) return false;
    if (!bankNumber || !branchNumber || !accountNumber) return false;
    return (
      isDigitsOnly(bankNumber) &&
      isDigitsOnly(branchNumber) &&
      isDigitsOnly(accountNumber)
    );
  }, [plan, bankNumber, branchNumber, accountNumber]);

  const handleClose = () => {
    if (loading) return;
    onHide();
  };

  const handleApprove = async () => {
    if (!plan || !isValid) return;

    const ok =
      await updateInstallmentPlanBankAccountDetailsBankTransferOperation(
        user,
        plan.installmentPlan.id,
        { bankNumber, branchNumber, accountNumber },
        setLoading,
        handleClose,
      );

    if (!ok) return;
  };

  return (
    <Dialog
      header="עדכון פרטי בנק להוראת קבע"
      visible={visible && plan !== null}
      onHide={handleClose}
      style={{ width: '350px', direction: 'rtl' }}
      modal
      closable={!loading}
      footer={
        <div
          style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
        >
          <Button
            label="ביטול"
            icon="pi pi-times"
            severity="secondary"
            text
            onClick={handleClose}
            disabled={loading}
          />
          <Button
            label="שמור"
            icon="pi pi-check"
            severity="info"
            onClick={handleApprove}
            disabled={!isValid || loading}
            loading={loading}
          />
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div
          style={{
            padding: '12px 14px',
            backgroundColor: '#EFF6FF',
            borderRadius: '10px',
            border: '1px solid #BFDBFE',
            fontSize: '14px',
            color: '#1E3A8A',
            lineHeight: 1.6,
          }}
        >
          שינוי פרטי הבנק ישפיע על הוראת הקבע החל מהגבייה הבאה. תשלומים שכבר
          נגבו/נכשלו לא ישתנו.
        </div>

        <div className="grid" style={{ display: 'grid', gap: '12px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontWeight: 600, color: '#374151' }}>
              מספר בנק
            </label>
            <InputText
              value={bankNumber}
              onChange={(e) => setBankNumber(e.target.value)}
              disabled={loading}
              inputMode="numeric"
              style={{ width: '100%', direction: 'rtl' }}
              placeholder="לדוגמה: 12"
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontWeight: 600, color: '#374151' }}>
              מספר סניף
            </label>
            <InputText
              value={branchNumber}
              onChange={(e) => setBranchNumber(e.target.value)}
              disabled={loading}
              inputMode="numeric"
              style={{ width: '100%', direction: 'rtl' }}
              placeholder="לדוגמה: 123"
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontWeight: 600, color: '#374151' }}>
              מספר חשבון
            </label>
            <InputText
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              disabled={loading}
              inputMode="numeric"
              style={{ width: '100%', direction: 'rtl' }}
              placeholder="לדוגמה: 1234567"
            />
          </div>
        </div>

        {!isValid && plan ? (
          <div style={{ fontSize: '13px', color: '#B91C1C' }}>
            חובה למלא את כל השדות. ניתן להזין ספרות בלבד.
          </div>
        ) : null}
      </div>
    </Dialog>
  );
};
